export type PlatformMode = 'public' | 'technical'

export type ModeCopy = Record<PlatformMode, string>

export type PlatformHeroContent = {
  eyebrow: ModeCopy
  subline: ModeCopy
  intro: ModeCopy
  summaryLabel: ModeCopy
  summaryItems: ModeCopy[]
  scopeLabel: ModeCopy
  scopeBody: ModeCopy
}

export type PlatformSectionIntro = {
  heading: string
  intro: ModeCopy
}

export type PlatformStatusItem = {
  id: string
  title: ModeCopy
  detail: ModeCopy
}

export type PlatformPrinciple = {
  id: string
  title: ModeCopy
  description: ModeCopy
}

export type PlatformFlowStep = {
  id: string
  title: ModeCopy
  description: ModeCopy
}

export type PlatformCapability = {
  id: string
  title: ModeCopy
  description: ModeCopy
}

export type PlatformProductProof = {
  id: string
  kind: 'image' | 'stack'
  imageSrc?: string
  imageAlt?: string
  label: string
  title: ModeCopy
  description: ModeCopy
  layout: 'lead' | 'support'
}

export type PlatformBoundary = {
  id: string
  title: ModeCopy
  description: ModeCopy
}

export type PlatformRoadmapColumn = {
  id: string
  title: ModeCopy
  items: ModeCopy[]
}

export const PLATFORM_HERO_CONTENT: PlatformHeroContent = {
  eyebrow: {
    public: 'Public reference',
    technical: 'Public reference',
  },
  subline: {
    public: 'A source-aware history platform for grounded conversation, place context, and visible evidence.',
    technical:
      'A source-aware history platform where retrieval, context release, and review surfaces shape what the model can responsibly say.',
  },
  intro: {
    public:
      'Places in Time combines historical places, figures, and conversation in a product experience designed to keep evidence in view.',
    technical:
      'Places in Time combines historical places, figures, and conversation in a system where generation stays downstream of retrieval, context framing, and separate review surfaces.',
  },
  summaryLabel: {
    public: 'At a glance',
    technical: 'At a glance',
  },
  summaryItems: [
    {
      public: 'Conversation stays tied to selected historical context.',
      technical: 'Conversation is bounded by active context releases.',
    },
    {
      public: 'Evidence cues remain visible beside the answer.',
      technical: 'Review and provenance surfaces stay distinct from answer delivery.',
    },
  ],
  scopeLabel: {
    public: 'Reference scope',
    technical: 'Reference scope',
  },
  scopeBody: {
    public:
      'This page describes the platform as it exists today: the current public experience, the principles behind it, and the operational limits that shape how it grows.',
    technical:
      'This page describes the currently active product model: public experience, retrieval and review principles, and the operating boundaries that govern release and expansion.',
  },
}

export const PLATFORM_SECTION_INTROS: Record<
  'proof' | 'principles' | 'process' | 'capabilities' | 'boundaries' | 'roadmap',
  PlatformSectionIntro
> = {
  proof: {
    heading: 'How it behaves in practice',
    intro: {
      public:
        'The live product is the clearest explanation. These examples show how conversation, context, and system structure appear in practice.',
      technical:
        'The live product is the clearest starting point. These examples show the visible interface and the system layers behind it.',
    },
  },
  principles: {
    heading: 'Core Principles',
    intro: {
      public:
        'A small set of operating choices keeps the platform grounded and legible.',
      technical:
        'A small set of operating choices constrains generation and keeps interpretation inspectable.',
    },
  },
  process: {
    heading: 'How the Platform Works',
    intro: {
      public:
        'A response follows a bounded sequence from question framing to delivery.',
      technical:
        'A response moves through bounded stages: session framing, context release selection, retrieval, drafting, and separate review-aware delivery.',
    },
  },
  capabilities: {
    heading: 'Current Platform Capabilities',
    intro: {
      public:
        'The current platform already supports a coherent public experience. These are the capabilities available in the live product and release workflow.',
      technical:
        'The live platform already supports a coherent public experience across chat, context control, evidence framing, and release discipline.',
    },
  },
  boundaries: {
    heading: 'Operational Boundaries',
    intro: {
      public:
        'Trust also depends on what the platform does not expose or automate. These boundaries keep public output legible while protecting review and governance workflows.',
      technical:
        'Trust also depends on which control-plane details remain private and which safeguards must stay upstream of public release.',
    },
  },
  roadmap: {
    heading: 'Near-Term Development',
    intro: {
      public:
        'The next phase is focused on clearer provenance language, stronger release visibility, and gradual expansion of institutional controls and quality monitoring.',
      technical:
        'Near-term work is focused on sharper epistemic cues, better release observability, and broader policy and evaluation coverage.',
    },
  },
}

export const PLATFORM_PRODUCT_PROOF: PlatformProductProof[] = [
  {
    id: 'conversation',
    kind: 'image',
    imageSrc: '/images/home/Chat demo.png',
    imageAlt: 'Places in Time conversation view showing a user question and historically framed response.',
    label: 'Example 01',
    layout: 'lead',
    title: {
      public: 'Conversation shaped by historical framing',
      technical: 'Conversation shaped by historical framing',
    },
    description: {
      public:
        'Users can ask natural questions, but responses remain shaped by selected historical context rather than open-ended improvisation.',
      technical:
        'Users can ask natural questions, but output stays inside the active historical frame. Fluency is downstream of the selected context boundary.',
    },
  },
  {
    id: 'context',
    kind: 'image',
    imageSrc: '/images/home/Chat demo3.png',
    imageAlt: 'Places in Time interface showing context and source information alongside a conversation.',
    label: 'Example 02',
    layout: 'support',
    title: {
      public: 'Context remains visible',
      technical: 'Context remains visible',
    },
    description: {
      public:
        'Answers are not treated as black-box output. Supporting context and referenced works remain visible beside the conversation.',
      technical:
        'Supporting context and referenced works remain visible beside the conversation, keeping provenance and retrieval framing in view.',
    },
  },
  {
    id: 'stack',
    kind: 'stack',
    label: 'Example 03',
    layout: 'support',
    title: {
      public: 'System layers stay separate',
      technical: 'Current stack and control surfaces',
    },
    description: {
      public:
        'The platform does not collapse interface, evidence, and review into one layer. Conversation sits on top of a clearer system boundary.',
      technical:
        'The interface, retrieval layer, active context release, and review surface remain distinct. That separation is what makes the system inspectable rather than opaque.',
    },
  },
]

export const PLATFORM_STATUS_STRIP: PlatformStatusItem[] = [
  {
    id: 'experience',
    title: {
      public: 'Live public experience',
      technical: 'Live public experience',
    },
    detail: {
      public: 'Visitors can already explore places, figures, and source-aware conversation in the product.',
      technical: 'The public product flow is live across places, figures, and source-aware conversation.',
    },
  },
  {
    id: 'context',
    title: {
      public: 'Versioned context releases',
      technical: 'Versioned context releases',
    },
    detail: {
      public: 'Historical context can be updated through deliberate releases instead of invisible drift.',
      technical: 'Retrieval boundaries are defined by active, versioned context releases.',
    },
  },
  {
    id: 'review',
    title: {
      public: 'Separate review surface',
      technical: 'Separate review surface',
    },
    detail: {
      public: 'Interpretive cues are kept distinct from the main conversational answer.',
      technical: 'Review-layer output is separated from persona-style response delivery.',
    },
  },
  {
    id: 'release',
    title: {
      public: 'Release discipline',
      technical: 'Release discipline',
    },
    detail: {
      public: 'Build and lint checks remain part of the release path for platform updates.',
      technical: 'Lint and build validation remain part of the release path for platform updates.',
    },
  },
]

export const PLATFORM_PRINCIPLES: PlatformPrinciple[] = [
  {
    id: 'source-aware',
    title: {
      public: 'Source-aware by design',
      technical: 'Source-aware by design',
    },
    description: {
      public: 'Historical conversation begins from selected context, not free-form generation alone.',
      technical: 'Historical conversation begins from selected retrieval context rather than unconstrained generation.',
    },
  },
  {
    id: 'provenance',
    title: {
      public: 'Interpretation stays inspectable',
      technical: 'Interpretation stays inspectable',
    },
    description: {
      public: 'Evidence cues stay visible so readers can check how an answer is being framed.',
      technical: 'Evidence framing and provenance cues stay visible so output can be interpreted against retrieval context.',
    },
  },
  {
    id: 'release-control',
    title: {
      public: 'Change stays deliberate',
      technical: 'Change stays deliberate',
    },
    description: {
      public: 'New context, domains, and interface controls are introduced through staged release rather than being opened all at once.',
      technical: 'New context releases, domains, and controls are introduced through staged activation rather than broad default enablement.',
    },
  },
]

export const PLATFORM_FLOW_STEPS: PlatformFlowStep[] = [
  {
    id: 'question',
    title: {
      public: 'Question framing',
      technical: 'Question framing',
    },
    description: {
      public: 'The platform begins by reading the user request within the current conversation and historical setting.',
      technical: 'Session intent, persona context, and conversation state are normalized before retrieval begins.',
    },
  },
  {
    id: 'release',
    title: {
      public: 'Context selection',
      technical: 'Context release selection',
    },
    description: {
      public: 'The response is bounded by an active context set chosen for the current experience.',
      technical: 'The response boundary is defined by the active versioned context release.',
    },
  },
  {
    id: 'retrieval',
    title: {
      public: 'Evidence assembly',
      technical: 'Evidence assembly',
    },
    description: {
      public: 'Relevant evidence is gathered before answer writing begins.',
      technical: 'Retrieval composes a bounded evidence bundle before response drafting begins.',
    },
  },
  {
    id: 'answer',
    title: {
      public: 'Response drafting',
      technical: 'Response drafting',
    },
    description: {
      public: 'The model writes within that selected evidence frame rather than inventing context from scratch.',
      technical: 'The model drafts output downstream of the selected evidence frame rather than inventing context from scratch.',
    },
  },
  {
    id: 'delivery',
    title: {
      public: 'Delivery and review cues',
      technical: 'Delivery and review cues',
    },
    description: {
      public: 'Readers receive the answer alongside the cues needed to understand provenance, framing, and review status.',
      technical: 'Readers receive the answer alongside provenance signals and separate review-layer cues.',
    },
  },
]

export const PLATFORM_CAPABILITIES: PlatformCapability[] = [
  {
    id: 'threads',
    title: {
      public: 'Guest and account conversation flows',
      technical: 'Guest and account conversation flows',
    },
    description: {
      public: 'The platform supports both guest sessions and account-based conversation journeys in a shared product surface.',
      technical: 'The platform supports shared guest and account thread entry with consistent experience controls.',
    },
  },
  {
    id: 'persona',
    title: {
      public: 'Persona-aware historical conversation',
      technical: 'Persona-aware historical conversation',
    },
    description: {
      public: 'Visitors can move through historically framed conversation modes without losing consistency across the experience.',
      technical: 'Persona-mode response behavior is integrated into the thread experience with consistent control continuity.',
    },
  },
  {
    id: 'context',
    title: {
      public: 'Versioned context management',
      technical: 'Versioned context management',
    },
    description: {
      public: 'Context sets can be updated, revised, or retired without rewriting the whole product.',
      technical: 'Context releases can be activated, revised, or retired independently of model updates.',
    },
  },
  {
    id: 'evidence',
    title: {
      public: 'Evidence and provenance cues',
      technical: 'Evidence and provenance cues',
    },
    description: {
      public: 'Answer presentation includes cues that keep source framing visible to the reader.',
      technical: 'Response rendering includes evidence-bundle framing and provenance-oriented user cues.',
    },
  },
  {
    id: 'review',
    title: {
      public: 'Separate review layer',
      technical: 'Separate review layer',
    },
    description: {
      public: 'Interpretive review remains distinct from the main answer so readers can tell what is response and what is qualification.',
      technical: 'Review-layer behavior remains distinct from persona answer flow in the UI contract.',
    },
  },
  {
    id: 'release-discipline',
    title: {
      public: 'Release checks and controlled publication',
      technical: 'Release checks and controlled publication',
    },
    description: {
      public: 'Platform updates move through practical build and lint checks alongside controlled publication timing.',
      technical: 'Platform updates move through repeatable lint and build checks alongside controlled publication timing.',
    },
  },
]

export const PLATFORM_OPERATIONAL_BOUNDARIES: PlatformBoundary[] = [
  {
    id: 'public-surface',
    title: {
      public: 'Public output is intentionally scoped',
      technical: 'Public output is intentionally scoped',
    },
    description: {
      public: 'Public-facing views expose product-safe provenance cues rather than raw internal control interfaces.',
      technical: 'Public-facing views expose product-safe provenance cues rather than internal control-plane detail.',
    },
  },
  {
    id: 'release-checkpoints',
    title: {
      public: 'Context changes follow release checkpoints',
      technical: 'Context changes follow release checkpoints',
    },
    description: {
      public: 'Context updates are reviewed and activated deliberately before they become part of the broader experience.',
      technical: 'Context releases pass staged review and activation checkpoints before broad rollout.',
    },
  },
  {
    id: 'governance',
    title: {
      public: 'Governance remains upstream of presentation',
      technical: 'Governance remains upstream of presentation',
    },
    description: {
      public: 'Evidence framing is designed for clarity without turning public pages into a window onto internal governance workflows.',
      technical: 'Evidence framing stays legible without exposing governance internals on public surfaces.',
    },
  },
  {
    id: 'expansion',
    title: {
      public: 'Expansion stays deliberate',
      technical: 'Expansion stays deliberate',
    },
    description: {
      public: 'New capabilities are introduced when they can be reviewed, framed, and supported well, not simply when they are technically possible.',
      technical: 'New capabilities are promoted when they can be reviewed, framed, and supported well, not simply when they are technically possible.',
    },
  },
]

export const PLATFORM_ROADMAP: PlatformRoadmapColumn[] = [
  {
    id: 'near',
    title: {
      public: 'Current focus',
      technical: 'Current focus',
    },
    items: [
      {
        public: 'Refine provenance wording in chat so interpretation is easier to read in public-facing use.',
        technical: 'Refine provenance and review wording in chat so epistemic signals are easier to read.',
      },
      {
        public: 'Publish clearer notes when active context releases change.',
        technical: 'Expose clearer product-safe notes when active context releases change.',
      },
      {
        public: 'Strengthen guidance around viewpoint balance for sensitive historical topics.',
        technical: 'Strengthen viewpoint-balance controls and guidance for sensitive historical topics.',
      },
    ],
  },
  {
    id: 'next',
    title: {
      public: 'Next layer',
      technical: 'Next layer',
    },
    items: [
      {
        public: 'Add more workspace controls for institutional use and shared stewardship.',
        technical: 'Add workspace-level policy and lifecycle controls for institutional use and shared stewardship.',
      },
      {
        public: 'Improve auditability around context snapshots and revision history.',
        technical: 'Improve auditability around context snapshots, revision history, and release traceability.',
      },
      {
        public: 'Expand evaluation and quality monitoring across more historical domains.',
        technical: 'Expand retrieval evaluation and quality monitoring across more historical domains.',
      },
    ],
  },
]
